import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import styles from "./CheckInInterface.module.css";
import { client } from "../../appSync/appSyncClient";
import { gql } from "graphql-request";

interface Ticket {
  paymentId: string;
  status: string;
  email?: string;
  ticketCount?: number;
  vegDinner?: number;
  nonVegDinner?: number;
  checkedIn?: boolean;
  checkedInAt?: string;
}

interface VerifyTicketResponse {
  verifyTicket: {
    success: boolean;
    ticket?: Ticket;
    message: string;
  };
}

interface CheckInTicketResponse {
  checkInTicket: {
    success: boolean;
    ticket?: Ticket;
    message: string;
  };
}

const CheckInInterface: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [paymentId, setPaymentId] = useState("");
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState<"success" | "error" | "info">(
    "info",
  );

  useEffect(() => {
    const idFromUrl = searchParams.get("id");
    if (idFromUrl) {
      setPaymentId(idFromUrl);
    }
  }, [searchParams]);

  const VERIFY_TICKET_QUERY = gql`
    query VerifyTicket($paymentId: String!) {
      verifyTicket(paymentId: $paymentId) {
        success
        message
        ticket {
          paymentId
          status
          email
          ticketCount
          vegDinner
          nonVegDinner
          checkedIn
          checkedInAt
        }
      }
    }
  `;

  const CHECK_IN_TICKET_MUTATION = gql`
    mutation CheckInTicket($paymentId: String!) {
      checkInTicket(paymentId: $paymentId) {
        success
        message
        ticket {
          paymentId
          status
          email
          ticketCount
          vegDinner
          nonVegDinner
          checkedIn
          checkedInAt
        }
      }
    }
  `;

  const handleVerifyTicket = async () => {
    if (!paymentId.trim()) {
      setMessage("Please enter or scan a payment ID");
      setMessageType("error");
      return;
    }

    setIsLoading(true);
    setMessage("");

    try {
      const result = (await client.request(VERIFY_TICKET_QUERY, {
        paymentId: paymentId.trim(),
      })) as VerifyTicketResponse;

      const { success, ticket, message: responseMessage } = result.verifyTicket;

      if (success && ticket) {
        setTicket(ticket);
        setMessage(responseMessage);
        setMessageType("success");
      } else {
        setTicket(null);
        setMessage(responseMessage || "Failed to verify ticket");
        setMessageType("error");
      }
    } catch (err) {
      console.error("Error verifying ticket:", err);
      setMessage("Error verifying ticket. Please try again.");
      setMessageType("error");
      setTicket(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckIn = async () => {
    if (!ticket?.paymentId) {
      setMessage("No ticket to check in");
      setMessageType("error");
      return;
    }

    setIsLoading(true);
    setMessage("");

    try {
      const result = (await client.request(CHECK_IN_TICKET_MUTATION, {
        paymentId: ticket.paymentId,
      })) as CheckInTicketResponse;

      const {
        success,
        ticket: updatedTicket,
        message: responseMessage,
      } = result.checkInTicket;

      if (success && updatedTicket) {
        setTicket(updatedTicket);
        setMessage(responseMessage);
        setMessageType("success");
      } else {
        setMessage(responseMessage || "Failed to check in ticket");
        setMessageType("error");
      }
    } catch (err) {
      console.error("Error checking in ticket:", err);
      setMessage("Error checking in ticket. Please try again.");
      setMessageType("error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    setPaymentId("");
    setTicket(null);
    setMessage("");
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <h1 className={styles.title}>Event Check-In</h1>

        {/* Input Section */}
        <div className={styles.inputSection}>
          <label htmlFor="paymentId" className={styles.label}>
            Payment ID / Confirmation Number
          </label>
          <p className={styles.hint}>
            Scan the QR code on your ticket or enter the confirmation number
          </p>
          <input
            id="paymentId"
            type="text"
            value={paymentId}
            onChange={(e) => setPaymentId(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleVerifyTicket()}
            placeholder="e.g., bce90f6c-9556-410b-aa35-772e2aa145f9"
            className={styles.input}
            disabled={isLoading}
          />
          <button
            onClick={handleVerifyTicket}
            disabled={isLoading || !paymentId.trim()}
            className={styles.verifyButton}
          >
            {isLoading ? "Verifying..." : "Verify Ticket"}
          </button>
        </div>

        {/* Message Display */}
        {message && (
          <div className={`${styles.message} ${styles[messageType]}`}>
            {message}
          </div>
        )}

        {/* Ticket Details Section */}
        {ticket && (
          <div className={styles.ticketSection}>
            <h2 className={styles.sectionTitle}>Ticket Details</h2>

            <div className={styles.detailsGrid}>
              <div className={styles.detailItem}>
                <span className={styles.label}>Payment ID</span>
                <span className={styles.value}>
                  {ticket.paymentId.slice(0, 8)}...
                </span>
              </div>

              <div className={styles.detailItem}>
                <span className={styles.label}>Status</span>
                <span
                  className={`${styles.value} ${styles[ticket.status.toLowerCase()]}`}
                >
                  {ticket.status}
                </span>
              </div>

              <div className={styles.detailItem}>
                <span className={styles.label}>Email</span>
                <span className={styles.value}>{ticket.email || "N/A"}</span>
              </div>

              <div className={styles.detailItem}>
                <span className={styles.label}>Admission Tickets</span>
                <span className={styles.value}>{ticket.ticketCount || 0}</span>
              </div>

              {ticket.vegDinner ? (
                <div className={styles.detailItem}>
                  <span className={styles.label}>Vegetarian Dinners</span>
                  <span className={styles.value}>{ticket.vegDinner}</span>
                </div>
              ) : null}

              {ticket.nonVegDinner ? (
                <div className={styles.detailItem}>
                  <span className={styles.label}>Non-Veg Dinners</span>
                  <span className={styles.value}>{ticket.nonVegDinner}</span>
                </div>
              ) : null}

              <div className={styles.detailItem}>
                <span className={styles.label}>Check-In Status</span>
                <span
                  className={`${styles.value} ${ticket.checkedIn ? styles.checkedIn : styles.pending}`}
                >
                  {ticket.checkedIn ? "✓ Checked In" : "Pending"}
                </span>
              </div>

              {ticket.checkedInAt && (
                <div className={styles.detailItem}>
                  <span className={styles.label}>Checked In At</span>
                  <span className={styles.value}>
                    {new Date(ticket.checkedInAt).toLocaleString()}
                  </span>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className={styles.actions}>
              {!ticket.checkedIn ? (
                <button
                  onClick={handleCheckIn}
                  disabled={isLoading}
                  className={styles.checkInButton}
                >
                  {isLoading ? "Checking In..." : "Check In Guest"}
                </button>
              ) : (
                <div className={styles.checkedInMessage}>
                  ✓ This ticket has already been checked in at{" "}
                  {new Date(ticket.checkedInAt!).toLocaleTimeString()}
                </div>
              )}

              <button
                onClick={handleClear}
                disabled={isLoading}
                className={styles.clearButton}
              >
                Clear
              </button>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!ticket && paymentId && !message && (
          <div className={styles.emptyState}>
            <p>Enter a payment ID and click "Verify Ticket" to see details</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckInInterface;
